import { ImageScaleView, View } from '@play-co/timestep-core/ui';
import i18n from 'src/lib/i18n/i18n';
import { Feature } from 'src/replicant/analytics';
import StateObserver from 'src/StateObserver';
import PopupMatch2BaseFullscreen from '../../minigame/match2-timestep/components/PopupMatch2BaseFullscreen';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { LangBitmapFontTextShadowView } from 'src/lib/ui/components/LangBitmapFontTextShadowView';
import { getStreakBattleRoyalePlayersAtMilestone } from '../streakbattleroyale.utils';
import StreakCloudShade from '../components/StreakCloudShade';
import gsap from 'gsap';
import { STREAK_EVENT_PLAYER_COUNT } from 'src/replicant/streakbattleroyale/streakbattleroyale.ruleset';
import StreakTapToContinue from '../components/StreakTapToContinue';
import {
  getStreakBattleRoyaleAssetPaths,
  streakBattleRoyaleTheme,
} from '../streakbattleroyale.theme';
import StreakGlare from '../components/StreakGlare';
import StreakMatchmakingPlayersV2 from '../components/StreakMatchmakingPlayersV2';
import { AssetGroup } from '@play-co/timestep-core';

export default class PopupStreakBattleRoyaleMatchmakingV2 extends PopupMatch2BaseFullscreen {
  private close: () => void;
  private backgroundView: View;
  private infoView: View;
  private title: ImageScaleView;
  private shade: StreakCloudShade;
  private players: StreakMatchmakingPlayersV2;
  private progress: LangBitmapFontTextShadowView;
  private message: LangBitmapFontTextView;
  private spinner: ImageScaleView;
  private glare: StreakGlare;

  constructor(opts: { superview: View; close: () => void }) {
    super({
      superview: opts.superview,
      zIndex: 10000,
      tracking: {
        feature: Feature.MATCH2.STREAK_EVENT._,
        $subFeature: Feature.MATCH2.STREAK_EVENT.MATCHMAKING_POPUP,
      },
    });

    this.close = opts.close;
    const { width, height } = this.getView().style;

    this.backgroundView = new View({
      superview: this.panel,
      x: width * 0.5,
    });

    // flat background
    new View({
      superview: this.backgroundView,
      width,
      height,
      x: -this.backgroundView.style.x,
      y: -this.backgroundView.style.y,
      backgroundColor: '#64D0FF',
    });

    // mountain
    const mountainHeight = (2085 * width) / 1080;
    new ImageScaleView({
      superview: this.backgroundView,
      image: streakBattleRoyaleTheme().getAsset('matchmaking_bg'),
      width,
      height: mountainHeight,
      y: mountainHeight / 2,
      centerOnOrigin: true,
    });

    let offsetY = 97;

    // Platform
    new ImageScaleView({
      superview: this.backgroundView,
      image: streakBattleRoyaleTheme().getAsset('platform'),
      width: 200,
      height: 81,
      y: height * 0.25 + offsetY,
      scale: 0.9,
      centerOnOrigin: true,
    });

    // Glare VFX
    this.glare = new StreakGlare({
      superview: this.backgroundView,
      opacity: 0.1,
      scale: 1,
      x: 0,
      y: height * 0.25 + offsetY - 76,
    });

    // Chest
    new ImageScaleView({
      superview: this.backgroundView,
      image: streakBattleRoyaleTheme().getAsset('chest'),
      scale: 0.8,
      width: 190,
      height: 214,
      x: -4,
      y: height * 0.25 + offsetY - 68,
      centerOnOrigin: true,
    });

    const locale = StateObserver.getLocale();
    const isEn = locale === 'en';

    this.title = new ImageScaleView({
      superview: this.panel,
      image: streakBattleRoyaleTheme().getAsset(`title_event_${locale}`),
      scaleMethod: 'contain',
      width: isEn ? 408 : 576,
      height: isEn ? 104 : 105,
      scale: isEn ? 1 : 0.85,
      x: width * 0.5,
      y: 120,
      centerOnOrigin: true,
    });

    this.shade = new StreakCloudShade({
      superview: this.panel,
      width,
      height,
    });

    this.infoView = new View({
      superview: this.panel,
      x: width * 0.5,
      y: height * 0.74,
    });

    this.players = new StreakMatchmakingPlayersV2({
      superview: this.infoView,
    });

    offsetY = 90;

    this.progress = new LangBitmapFontTextShadowView({
      superview: this.infoView,
      font: 'Title',
      localeText: () => '40/100',
      size: 50,
      align: 'center',
      verticalAlign: 'center',
      color: '#FFFFFF',
      width,
      height: 54,
      centerOnOrigin: true,
      y: offsetY,
      textShadowOffsetX: 0,
      textShadowOpacity: 0.4,
    });

    this.message = new LangBitmapFontTextView({
      superview: this.infoView,
      font: 'Body',
      localeText: () => i18n('match2.streakBattleRoyale.matchmaking.message'),
      size: 30,
      align: 'center',
      verticalAlign: 'center',
      color: '#277780',
      width,
      height: 54,
      centerOnOrigin: true,
      y: offsetY + 70,
    });

    this.spinner = new ImageScaleView({
      superview: this.infoView,
      image: streakBattleRoyaleTheme().getAsset('spinner'),
      width: 71,
      height: 70,
      centerOnOrigin: true,
      y: offsetY + 154,
    });
  }

  private reset() {
    this.shade.ratio = -0.5;
    this.progress.localeText = () => `1/${STREAK_EVENT_PLAYER_COUNT}`;
    this.title.updateOpts({ opacity: 0 });
    this.infoView.updateOpts({ opacity: 0 });
    this.spinner.updateOpts({ scale: 0 });
    this.backgroundView.updateOpts({ visible: false });
  }

  private async startIntroSequence() {
    const duration = 1;
    const fadeDuration = 0.6;
    const scaleDuration = 0.6;
    const spinDuration = 1;
    const targetShadeRatio = 0.38;

    // starts the spinner
    gsap.to(this.spinner.style, {
      duration: spinDuration,
      r: -Math.PI * 2,
      ease: 'none',
      repeat: -1,
    });

    await gsap
      .timeline()
      // pulls the shade up
      .to(this.shade, {
        duration,
        ratio: 1,
        ease: 'quad.inOut',
        onComplete: () => {
          this.backgroundView.updateOpts({ visible: true });
        },
      })
      // lowers the shade down to the target
      .to(this.shade, {
        duration: duration * 0.5,
        ratio: targetShadeRatio,
        ease: 'quad.out',
      })
      // fades in the title
      .to(
        this.title.style,
        {
          duration: fadeDuration,
          opacity: 1,
          ease: 'quad.inOut',
        },
        duration * 1.5,
      )
      // fades in the info view
      .to(
        this.infoView.style,
        {
          duration: fadeDuration,
          opacity: 1,
          ease: 'quad.inOut',
        },
        duration * 1.5 + fadeDuration * 0.5,
      )
      // shows the spinner
      .to(this.spinner.style, {
        duration: scaleDuration,
        scale: 1,
        ease: 'back.out',
      });
  }

  private async startFakeLoadingSequence() {
    const fakeProgress = { value: 1, floored: 1 };
    const duration = 1.5;
    const scaleDuration = 0.2;
    const onUpdate = () => {
      const newFloored = Math.floor(fakeProgress.value);
      const hasIncremented = newFloored > fakeProgress.floored;
      fakeProgress.floored = newFloored;
      this.progress.localeText = () =>
        `${Math.floor(fakeProgress.floored)}/${STREAK_EVENT_PLAYER_COUNT}`;

      if (!hasIncremented) return;

      // quick pulse effect for every count that goes up
      gsap.fromTo(
        this.progress.style,
        { scale: 1.2 },
        {
          duration: scaleDuration,
          scale: 1,
          ease: 'quad.out',
          overwrite: true,
        },
      );

      // Set progress label to green when at 100%
      if (fakeProgress.floored === STREAK_EVENT_PLAYER_COUNT) {
        this.progress.updateOpts({ color: '#EA583B' });
      }

      const showPer = Math.floor(
        STREAK_EVENT_PLAYER_COUNT / this.players.count,
      );

      // show next fake avatar after each threshold
      if (fakeProgress.floored % showPer === 0) {
        this.players.showNextPlayer();
      }
    };

    // Simulate loading progression
    await gsap.to(fakeProgress, {
      duration,
      value: STREAK_EVENT_PLAYER_COUNT,
      ease: 'quad.inOut',
      onUpdate,
    });

    await this.onLoaded();
  }

  private async onLoaded() {
    const duration = 0.5;

    // hide the spinner
    await gsap
      .timeline()
      .to(this.spinner.style, {
        duration,
        scale: 0,
        ease: 'back.in',
      })
      .to(
        this.message.style,
        {
          duration,
          opacity: 0,
          ease: 'quad.inOut',
        },
        '<',
      );

    new StreakTapToContinue({
      superview: this.panel,
      offsetY: -67,
      textOpts: { color: '#277780', textShadowOpacity: 0 },
      tracking: {
        feature: Feature.MATCH2.STREAK_EVENT._,
        $subFeature: Feature.MATCH2.STREAK_EVENT.MATCHMAKING_POPUP_CONTINUE,
      },
      onClick: () => this.close(),
    });

    // stops the spinner
    gsap.killTweensOf(this.spinner.style);
  }

  public init() {
    super.init({});
    const players = getStreakBattleRoyalePlayersAtMilestone(0);
    this.players.setup(players);
    this.reset();
  }

  public override async fadeIn() {
    this.setInteractivity(false);
    this.attachRoot();
    await this.startIntroSequence();
    await this.startFakeLoadingSequence();
    await this.setInteractivity(true);
  }

  public override async fadeOut() {
    this.setInteractivity(false);

    // fadeout info
    gsap.to(this.infoView.style, {
      duration: 0.4,
      opacity: 0,
      ratio: 1,
      ease: 'quad.inOut',
    });

    // roll the cloud shade up
    await gsap.to(this.shade, {
      duration: 0.5,
      ratio: 1,
      ease: 'quad.in',
      delay: 0.3,
    });

    // hide remaining elements and stop animations
    this.title.updateOpts({ visible: false });
    this.backgroundView.updateOpts({ visible: false });
    this.players.stopAnimation();
    this.glare.stopAnimation();

    // roll the cloud shade down before detaching the popup as a visual transition hack
    gsap.to(this.shade, {
      duration: 1,
      ratio: -0.5,
      ease: 'quad.in',
      onComplete: () => this.detachRoot(),
    });
  }

  protected getAssetsGroup() {
    const urls = getStreakBattleRoyaleAssetPaths();
    return new AssetGroup(urls);
  }
}
